/*  
    Developers: Sakshi Bhise  
    Script: ImpersonateMenu.tsx  
    Date: 06/18/2025  
    Project Name: AskHR  
    Team: DSET  
  
    Purpose of this script:    
  - UI component for editing Impersonate metadata such as Email. 
  - The entered email is used to fetch all the details related to impersonation i.e. user country, user work site, permission groups. 
  - Email typed should be present in DB, should be from supported country, should be Teva employee(employee group Employee).
  - Integrates with i18n translation hooks.    
  - Designed for use in modals and forms in the AskHR workflow.    
  - Dialog matches support impersonation UI, including error styles and button layout.     
*/
// ======================= Imports ======================= //  
// Importing required components and hooks from Fluent UI and React.  
import { Icon, TextField } from "@fluentui/react"; // UI controls  
import styles from "./ImpersonateMenu.module.css";      // CSS module for styling  
import { PrimaryButton, DefaultButton, Stack } from "@fluentui/react"; // Buttons and layout  
import { Button, InputBox } from "@teva/ui";

// ======================= Props Interface ======================= //  
// Strongly typed(defined type of value of attribute/props) props(short for properties) for clarity and reliability in Impersonate Menu.  
interface ImpersonateMenuProps {
    settings: {
        Email: string; // User email to impersonate  eg. test.user@tevapharm.com
    };
    onSettingsChange: (event: any) => void; // Handler for any field change
    onImpersonate: () => void;              // Called when starting impersonation
    onExitImpersonation: () => void;        // Called when exiting impersonation
    error?: string;                         // Optional error message to display
    isLoading?: boolean;                    // Disables submit while request is in-flight
}

// ======================= Component Definition ======================= //  
const ImpersonateMenu = ({
    settings,             // Current settings (mainly user email)
    onSettingsChange,     // Callback when settings change
    onImpersonate,        // Callback to start impersonation
    onExitImpersonation,  // Callback to exit impersonation
    error,                // Error message (if any)
    isLoading = false,
}: ImpersonateMenuProps) => {

    // ======================= Field Change Handler ======================= //  
    // Handles change events for the fields (currently only Email(text field)).  
    // Wraps the change into an object structure expected by parent.  
    const handleFieldChange = (fieldName: string, value: any) => {
        const event = {
            target: {
                name: fieldName,
                value: value,
            },
        };
        onSettingsChange(event);
    };

    // ======================= Render Component Layout ======================= //  
    return (
        <div>
            {/* ========== Email Field ========== */}
            <div className={styles.field}>
                <InputBox
                    label="User Email"
                    isRequired={true}
                    placeholder="Enter the Email"           // Placeholder hint
                    value={settings.Email}
                    type="email"
                    onChange={(newValue) => handleFieldChange("Email", newValue || "")}
                />
            </div>

            {/* ========== Error Message Box (Shown only if error is present) ========== */}
            {error && (
                <div className={styles.errorBox}>
                    <Icon iconName="ErrorBadge" className={styles.errorIcon} />
                    {/* ErrorBadge icon (visually indicates error) */}
                    <span>{error}</span>
                </div>
            )}
            {/*   
                .errorBox uses:  
                  background: #ffeaea;   // Misty Rose (soft red hint for error background)  
                  border: 1px solid #f4cccc; // Pale Pink (border for error box)  
                  color: #d32f2f;        // Crimson Red (error text)  
            */}

            {/* ========== Action Buttons ========== */}
            <Stack horizontal tokens={{ childrenGap: 12 }} className={styles.buttonRow}>
                <Button variant="default" label="Cancel" onClick={onExitImpersonation} fluentProps={{ styles: { root: { borderRadius: 8 } } }} />
                <Button variant="primary" label="Start Support" onClick={onImpersonate} isDisabled={isLoading} fluentProps={{ styles: { root: { borderRadius: 8 } } }} />
            </Stack>
            {/*   
                Stack lays out the buttons horizontally with 12px gap.  
                .buttonRow can be used for additional styling, e.g. aligning right.  
            */}
        </div>
    );
};

// Exporting the component for use in other parts of the app.  
export { ImpersonateMenu };  